import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert,
    Button,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Upload,
    message
} from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { kedoGet, kedoUploadDocument } from './kedoApi';

const DOC_TYPES = [
    { value: 'contract', label: 'Договор' },
    { value: 'act', label: 'Акт' },
    { value: 'upd', label: 'УПД' },
    { value: 'hr_order', label: 'Кадровый' },
    { value: 'other', label: 'Произвольный' }
];

const statusColor = {
    draft: 'default',
    in_review: 'processing',
    pending_sign: 'orange',
    signed: 'green',
    rejected: 'red',
    archived: 'default'
};

const KedoDocumentsPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [form] = Form.useForm();
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const query = useMemo(() => {
        const q = {};
        ['status', 'direction', 'filter'].forEach(k => {
            if (params.get(k)) q[k] = params.get(k);
        });
        return q;
    }, [params]);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await kedoGet('/documents', { params: query });
            setRows(Array.isArray(data) ? data : data?.items || []);
        } catch (e) {
            setError(
                e?.response?.data?.message || e.message || 'Ошибка загрузки'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        if (params.get('new') === '1') setOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.toString()]);

    const onCreate = async values => {
        try {
            const file = fileList[0]?.originFileObj;
            if (!file) {
                message.warning('Прикрепите файл документа');
                return;
            }
            const external_refs = {};
            if (values.registry_id) {
                external_refs.registry_id = Number(values.registry_id);
            }
            if (values.task_id) {
                external_refs.task_id = Number(values.task_id);
            }
            if (values.counterparty_inn) {
                external_refs.counterparty_inn = values.counterparty_inn;
            }
            const doc = await kedoUploadDocument({
                title: values.title,
                doc_type: values.doc_type || 'other',
                description: values.description || '',
                external_refs,
                file
            });
            message.success('Документ создан');
            setOpen(false);
            form.resetFields();
            setFileList([]);
            navigate(`/kedo/app/documents/${doc.id}`);
        } catch (e) {
            message.error(
                e?.response?.data?.message || e.message || 'Не удалось создать'
            );
        }
    };

    const columns = [
        {
            title: '№',
            dataIndex: 'id',
            width: 70,
            render: id => <Link to={`/kedo/app/documents/${id}`}>{id}</Link>
        },
        {
            title: 'Название',
            dataIndex: 'title',
            render: (t, row) => (
                <Link to={`/kedo/app/documents/${row.id}`}>{t}</Link>
            )
        },
        {
            title: 'Тип',
            dataIndex: 'doc_type',
            width: 110
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 130,
            render: s => <Tag color={statusColor[s] || 'default'}>{s}</Tag>
        },
        {
            title: 'Направление',
            dataIndex: 'direction',
            width: 110
        },
        {
            title: 'Обновлён',
            dataIndex: 'updated_at',
            width: 170,
            render: v => (v ? new Date(v).toLocaleString('ru-RU') : '—')
        }
    ];

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                    gap: 12,
                    flexWrap: 'wrap'
                }}
            >
                <div>
                    <h1 style={{ margin: 0, fontFamily: 'Georgia, serif' }}>
                        Документы
                    </h1>
                    <div style={{ opacity: 0.7, marginTop: 4 }}>
                        Внутренний ДО и обмен внутри платформы Kedo
                    </div>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setOpen(true)}
                >
                    Создать
                </Button>
            </div>

            {error ? (
                <Alert type="error" message={error} style={{ marginBottom: 12 }} />
            ) : null}

            <div className="kedo-panel">
                <Table
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    dataSource={rows}
                    pagination={{ pageSize: 20 }}
                    size="middle"
                />
            </div>

            <Modal
                title="Новый документ Kedo"
                open={open}
                onCancel={() => setOpen(false)}
                onOk={() => form.submit()}
                okText="Создать"
                destroyOnClose
            >
                <Form layout="vertical" form={form} onFinish={onCreate}>
                    <Form.Item
                        name="title"
                        label="Название"
                        rules={[{ required: true, message: 'Укажите название' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="doc_type" label="Тип" initialValue="contract">
                        <Select options={DOC_TYPES} />
                    </Form.Item>
                    <Form.Item name="description" label="Описание">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item label="Файл" required>
                        <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            fileList={fileList}
                            onChange={({ fileList: fl }) => setFileList(fl)}
                        >
                            <Button icon={<UploadOutlined />}>Выбрать файл</Button>
                        </Upload>
                    </Form.Item>
                    <Space wrap style={{ width: '100%' }}>
                        <Form.Item name="registry_id" label="registry_id (CRM)">
                            <Input placeholder="external_refs" />
                        </Form.Item>
                        <Form.Item name="task_id" label="task_id (CRM)">
                            <Input placeholder="external_refs" />
                        </Form.Item>
                        <Form.Item name="counterparty_inn" label="ИНН контрагента">
                            <Input />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </>
    );
};

export default KedoDocumentsPage;
